import {
  createDinero,
  Currency,
} from '@mr-yum/frontend-core/dist/services/createDinero'
import { AddIcon, cn, TickIcon } from '@mr-yum/frontend-ui'
import { CartStaleWrapper } from 'components/Cart/shared'
import { ItemPriceWrapper } from 'components/MenuItem/shared'
import { ItemQuantity } from 'components/MenuItem/utils'
import { Image } from 'components/Shared/Image'
import {
  CartItemPartsFragment,
  LoyaltyDiscountPartsFragment,
} from 'gql/graphql'
import React, { useRef, useState } from 'react'

type Props = {
  isLoading?: boolean
  cartItem: CartItemPartsFragment & {
    discounts?: LoyaltyDiscountPartsFragment[]
  }
  currency?: Currency
  onAddItem: (item: CartItemPartsFragment, quantity: number) => void
} & Partial<ItemQuantity>

export const ReorderDrinkItem = ({
  isLoading,
  cartItem,
  currency,
  onAddItem,
}: Props) => {
  const [added, setAdded] = useState(false)
  const cdnImage = cartItem.item?.cdnImage ?? cartItem.upsell?.menuItem.cdnImage

  const totalDiscount = cartItem.discounts?.reduce(
    (acc, discount) => acc + discount.amountInCents,
    0,
  )

  const displayPrice = createDinero(
    cartItem.totalInCents - (totalDiscount ?? 0),
    currency,
  ).toFormat()

  const clickCount = useRef(0)
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)

  const handleClick = () => {
    setAdded(true)

    setTimeout(() => {
      setAdded(false)
    }, 300)

    clickCount.current += 1

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    debounceTimeout.current = setTimeout(() => {
      onAddItem(cartItem, clickCount.current)
      clickCount.current = 0
    }, 500)
  }

  return (
    <CartStaleWrapper loading={isLoading}>
      <div className="flex flex-col justify-start gap-2">
        {cdnImage && (
          <div
            className="inset relative h-24 w-24 overflow-hidden rounded-lg border border-subtle"
            role="button"
            onClick={handleClick}
          >
            <Image
              alt=""
              image={{
                id: cdnImage.id,
                originalImagePath: cdnImage.originalImagePath,
                originalImageUrl: cdnImage.originalImageUrl,
              }}
              layout="fill"
              objectFit="cover"
            />
            <div className="absolute bottom-1 right-1 cursor-pointer overflow-hidden rounded-full bg-surface-ui-background bg-opacity-90 p-2 shadow">
              <div className="relative h-5 w-5">
                <div
                  className={cn(
                    'absolute transition-transform duration-300',
                    added ? '-translate-y-10' : 'translate-y-0 delay-300',
                  )}
                >
                  <AddIcon className="h-5 w-5" />
                </div>

                <div
                  className={cn(
                    'absolute transition-transform duration-300',
                    added ? 'translate-y-0' : 'translate-y-10 delay-300',
                  )}
                >
                  <TickIcon className="h-5 w-5" />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col text-left">
          <p className="mb-1 capitalize my-label-sm">{cartItem.name}</p>
          {currency &&
            (cartItem.discounts?.length ? (
              <div>
                <ItemPriceWrapper
                  data-testid="cart-item-total-display-price-wrapper"
                  className="items-center pr-4"
                >
                  <span
                    className="foreground-disabled mr-2 line-through"
                    data-testid="cart-item-original-total-display-price"
                  >
                    {createDinero(cartItem.totalInCents, currency).toFormat()}
                  </span>
                  <div
                    className="foreground-subtle"
                    data-testid="cart-item-total-display-price"
                  >
                    {displayPrice}
                  </div>
                </ItemPriceWrapper>
                {cartItem.discounts.map((d) => (
                  <p
                    key={d.name}
                    className="text-sm italic text-semantic-success-foreground-bold"
                    data-testid="cart-item-discount-name"
                  >
                    {`${d.name} ${createDinero(
                      d.amountInCents * -1,
                      currency,
                    ).toFormat()}`}
                  </p>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-between pr-4">
                <p
                  className="text-foreground-subtle my-body-sm"
                  data-testid="cart-item-total"
                >
                  {createDinero(cartItem.totalInCents, currency).toFormat()}
                </p>
              </div>
            ))}
        </div>
      </div>
    </CartStaleWrapper>
  )
}
